import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Shield, Lock, User, Mail, Phone, MapPin, DollarSign, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';

const LoanForm = ({ preLanderData, onVerifying }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();

  // Pre-fill employment data from prelander (but don't send to backend)
  useEffect(() => {
    if (preLanderData) {
      if (preLanderData[1]) setValue('loanAmount', preLanderData[1]);
      if (preLanderData[2]) setValue('employmentStatus', preLanderData[2]);
      if (preLanderData[3]) setValue('payFrequency', preLanderData[3]);
    }
  }, [preLanderData, setValue]);

  const agreedToTerms = watch('agreedToTerms');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    if (onVerifying) {
      onVerifying(data);
    }
  };

  const states = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  const InputField = ({ label, error, icon: Icon, ...props }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {Icon && <Icon className="w-4 h-4 inline mr-2 text-slate-400" />}
        {label}
      </label>
      <input
        className={`w-full px-4 py-3 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
          error ? 'border-red-300 focus:ring-red-200' : 'border-slate-300 focus:border-emerald-500 focus:ring-emerald-200'
        }`}
        {...props}
      />
      {error && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </p>
      )}
    </div>
  );

  const SelectField = ({ label, error, children, ...props }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <select
        className={`w-full px-4 py-3 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 transition-all ${
          error ? 'border-red-300 focus:ring-red-200' : 'border-slate-300 focus:border-emerald-500 focus:ring-emerald-200'
        }`}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-semibold text-slate-900">Loan Application</h1>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Lock className="w-4 h-4 text-emerald-600" />
              <span className="hidden sm:inline font-medium">Secure & Encrypted</span>
              <span className="sm:hidden font-medium">Secure</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Intro */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Complete Your Application</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                Please provide accurate information. All fields are required and your data is protected with bank-level security.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          
          {/* Personal Information */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Personal Information</h2>
                  <p className="text-sm text-slate-600">Your basic details</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InputField
                  label="First Name"
                  {...register('firstName', { required: 'First name is required', minLength: { value: 2, message: 'Too short' } })}
                  error={errors.firstName}
                  placeholder="John"
                />
                
                <InputField
                  label="Last Name"
                  {...register('lastName', { required: 'Last name is required', minLength: { value: 2, message: 'Too short' } })}
                  error={errors.lastName}
                  placeholder="Smith"
                />
              </div>
              
              <InputField
                label="Date of Birth"
                type="date"
                {...register('dateOfBirth', { 
                  required: 'Date of birth is required',
                  validate: (value) => {
                    const age = new Date().getFullYear() - new Date(value).getFullYear();
                    return age >= 18 || 'You must be at least 18 years old';
                  }
                })}
                error={errors.dateOfBirth}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
              />
              {!errors.dateOfBirth && (
                <p className="text-xs text-slate-500 -mt-3">You must be 18 years or older to apply</p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Contact Information</h2>
                  <p className="text-sm text-slate-600">How we'll reach you</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <InputField
                label="Email Address"
                icon={Mail}
                type="email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
                })}
                error={errors.email}
                placeholder="john.smith@email.com"
              />
              
              <InputField
                label="Phone Number"
                icon={Phone}
                type="tel"
                {...register('phone', { 
                  required: 'Phone number is required',
                  pattern: { value: /^[\d\s\-()]{10,}$/, message: 'Invalid phone number' }
                })}
                error={errors.phone}
                placeholder="(555) 123-4567"
                onInput={(e) => {
                  let v = e.target.value.replace(/\D/g, '');
                  if (v.length > 0) {
                    if (v.length <= 3) v = `(${v}`;
                    else if (v.length <= 6) v = `(${v.slice(0,3)}) ${v.slice(3)}`;
                    else v = `(${v.slice(0,3)}) ${v.slice(3,6)}-${v.slice(6,10)}`;
                  }
                  e.target.value = v;
                }}
              />
              {!errors.phone && (
                <p className="text-xs text-slate-500 -mt-3">We'll call or text you with your loan status</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Home Address</h2>
                  <p className="text-sm text-slate-600">Your residential address</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <InputField
                label="Street Address"
                {...register('address', { required: 'Street address is required' })}
                error={errors.address}
                placeholder="123 Main Street, Apt 4B"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <InputField
                  label="City"
                  {...register('city', { required: 'City is required' })}
                  error={errors.city}
                  placeholder="New York"
                />
                
                <SelectField
                  label="State"
                  {...register('state', { required: 'State is required' })}
                  error={errors.state}
                >
                  <option value="">Select state</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </SelectField>
                
                <InputField
                  label="ZIP Code"
                  {...register('zipCode', { 
                    required: 'ZIP code is required',
                    pattern: { value: /^\d{5}$/, message: 'Must be 5 digits' }
                  })}
                  error={errors.zipCode}
                  placeholder="12345"
                  maxLength="5"
                  onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ''); }}
                />
              </div>
            </div>
          </div>

          {/* Employment & Income */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Employment & Income</h2>
                  <p className="text-sm text-slate-600">Your financial information</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Loan Amount Requested</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: '1000', label: '$1,000' },
                    { value: '2500', label: '$2,500' },
                    { value: '5000', label: '$5,000' },
                    { value: '7500', label: '$7,500' }
                  ].map(amount => (
                    <label key={amount.value} className="relative">
                      <input
                        type="radio"
                        value={amount.value}
                        {...register('loanAmount', { required: 'Please select loan amount' })}
                        className="peer sr-only"
                      />
                      <div className="px-4 py-3 bg-white border-2 border-slate-300 peer-checked:border-emerald-600 peer-checked:bg-emerald-50 rounded-lg cursor-pointer text-center font-semibold text-slate-900 peer-checked:text-emerald-600 transition-all">
                        {amount.label}
                      </div>
                    </label>
                  ))}
                </div>
                {errors.loanAmount && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.loanAmount.message}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Monthly Income (After Taxes)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-medium">$</span>
                  <input
                    type="text"
                    {...register('monthlyIncome', { required: 'Monthly income is required' })}
                    className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.monthlyIncome ? 'border-red-300 focus:ring-red-200' : 'border-slate-300 focus:border-emerald-500 focus:ring-emerald-200'
                    }`}
                    placeholder="2,500"
                    onInput={(e) => {
                      let v = e.target.value.replace(/\D/g, '');
                      if (v) v = parseInt(v).toLocaleString();
                      e.target.value = v;
                    }}
                  />
                </div>
                {errors.monthlyIncome && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.monthlyIncome.message}
                  </p>
                )}
                {!errors.monthlyIncome && (
                  <p className="mt-1.5 text-xs text-slate-500">Your take-home pay each month</p>
                )}
              </div>
              
              <SelectField
                label="Employment Status"
                {...register('employmentStatus', { required: 'Employment status is required' })}
                error={errors.employmentStatus}
              >
                <option value="">Select your employment status</option>
                <option value="employed_fulltime">Employed Full-Time</option>
                <option value="employed_parttime">Employed Part-Time</option>
                <option value="self_employed">Self-Employed</option>
                <option value="benefits">Government Benefits</option>
                <option value="other">Other</option>
              </SelectField>
              
              <SelectField
                label="Pay Frequency"
                {...register('payFrequency', { required: 'Pay frequency is required' })}
                error={errors.payFrequency}
              >
                <option value="">How often are you paid?</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 Weeks</option>
                <option value="monthly">Monthly</option>
                <option value="other">Other</option>
              </SelectField>
            </div>
          </div>

          {/* Credit & Banking */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Credit & Banking</h2>
                  <p className="text-sm text-slate-600">Final verification details</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <SelectField
                label="Credit Score Range"
                {...register('creditScore', { required: 'Credit score is required' })}
                error={errors.creditScore}
              >
                <option value="">Select your credit range</option>
                <option value="excellent">Excellent (720-850)</option>
                <option value="good">Good (660-719)</option>
                <option value="fair">Fair (620-659)</option>
                <option value="poor">Poor (Below 620)</option>
                <option value="not-sure">Not Sure / No Credit History</option>
              </SelectField>
              {!errors.creditScore && (
                <p className="text-xs text-emerald-600 -mt-3 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  All credit types accepted • Won't affect your credit score
                </p>
              )}
              
              <SelectField
                label="Bank Account Type"
                {...register('bankAccountType', { required: 'Bank account type is required' })}
                error={errors.bankAccountType}
              >
                <option value="">Select account type</option>
                <option value="checking">Checking Account</option>
                <option value="savings">Savings Account</option>
                <option value="both">Both Checking & Savings</option>
                <option value="prepaid">Prepaid Card</option>
              </SelectField>
              
              {/* SSN Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <div className="flex items-start gap-3 mb-4">
                  <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Secure Identity Verification</h4>
                    <p className="text-xs text-slate-700 leading-relaxed">
                      For your security, we need the last 4 digits of your Social Security Number to verify your identity and prevent fraud.
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Last 4 Digits of SSN</label>
                  <input
                    type="text"
                    {...register('ssnLast4', { 
                      required: 'SSN last 4 digits required',
                      pattern: { value: /^\d{4}$/, message: 'Must be exactly 4 digits' }
                    })}
                    className={`w-full px-4 py-3 bg-white border rounded-lg font-mono text-lg tracking-widest text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.ssnLast4 ? 'border-red-300 focus:ring-red-200' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-200'
                    }`}
                    placeholder="••••"
                    maxLength="4"
                    onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ''); }}
                  />
                  {errors.ssnLast4 && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.ssnLast4.message}
                    </p>
                  )}
                  {!errors.ssnLast4 && (
                    <p className="mt-2 text-xs text-slate-600 flex items-center gap-1">
                      <Shield className="w-3 h-3 text-emerald-600" />
                      Protected with 256-bit bank-level encryption
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Submit */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 space-y-5">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    {...register('agreedToTerms', { required: true })}
                    className="mt-0.5 h-5 w-5 text-emerald-600 focus:ring-2 focus:ring-emerald-500 border-slate-300 rounded cursor-pointer"
                  />
                  <span className="text-sm text-slate-700 leading-relaxed">
                    I certify that I am at least 18 years old, a US resident, and the information provided is accurate. I consent to be contacted about loan offers via phone, email, or text. I have read and agree to the{' '}
                    <a href="/terms" className="text-emerald-600 font-semibold underline hover:text-emerald-700">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-emerald-600 font-semibold underline hover:text-emerald-700">Privacy Policy</a>.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !agreedToTerms}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white font-semibold py-4 px-6 rounded-lg transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20 disabled:shadow-none"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-3 border-white border-t-transparent"></div>
                    <span>Processing Application...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Submit Secure Application</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-4 flex-wrap text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span>256-bit SSL</span>
                </div>
                <div className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  <span>Bank Security</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  <span>100% Confidential</span>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mx-auto">
            This is a loan matching service. By submitting, you may be connected with lending partners. 
            Rates, terms, and approval vary by lender and state. Not all applicants qualify.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoanForm;
